import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { roleLabel } from "@/lib/roles";

export default function Settings() {
  const { user } = useAuth();
  const { data: org } = useQuery({ queryKey: ["org"], queryFn: async () => (await api.get("/meta/organization")).data });
  const { data: roles = [] } = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/meta/roles")).data });

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Platform · Settings"
        title="Organization & Roles"
        description="Multi-tenant configuration. This is where multi-company, multi-branch and multi-currency settings will live."
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-6">
        <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid="org-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Organization</div>
          <div className="font-heading text-2xl font-bold mt-1">{org?.legal_name || "—"}</div>
          <div className="grid md:grid-cols-4 gap-4 mt-6">
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">GSTIN</div><div className="mt-1 font-mono text-sm">{org?.gstin || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Country</div><div className="mt-1 text-sm">{org?.country}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Currency</div><div className="mt-1 text-sm">{org?.currency}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Timezone</div><div className="mt-1 text-sm font-mono">{org?.timezone}</div></div>
          </div>
        </div>

        <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid="me-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Your Account</div>
          <div className="font-heading text-xl font-bold mt-1">{user?.name}</div>
          <div className="text-sm text-zinc-500 mt-1 font-mono">{user?.email}</div>
          <div className="mt-3"><Badge variant="outline">{roleLabel(user?.role)}</Badge> <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">via {user?.provider}</span></div>
        </div>

        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="roles-list">
          <div className="px-6 py-4 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Roles in this Organization</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-6 py-3">Role Name</th><th className="px-6 py-3">Key</th><th className="px-6 py-3">Division</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {roles.map((r) => (
                <tr key={r.key}>
                  <td className="px-6 py-3 font-medium">{r.name}</td>
                  <td className="px-6 py-3 font-mono text-xs text-zinc-500">{r.key}</td>
                  <td className="px-6 py-3 text-zinc-600">{r.division}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-zinc-500 font-mono">
          Multi-company · multi-branch · multi-currency · SSO · SCIM · role editor → Sprint 5
        </div>
      </div>
    </div>
  );
}
